import { useState, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
type CitationStyle = 'APA' | 'MLA' | 'Chicago' | 'IEEE';
type SourceType = 'journal' | 'book' | 'website' | 'conference' | 'thesis' | 'newspaper';

interface ParsedReference {
  id: string;
  raw: string;
  sourceType: SourceType;
  authors: string[];
  year: string;
  title: string;
  journal?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  publisher?: string;
  city?: string;
  url?: string;
  doi?: string;
  accessDate?: string;
  edition?: string;
  conference?: string;
  institution?: string;
}

interface FormattedCitation {
  style: CitationStyle;
  fullCitation: string;
  inTextCitation: string;
}

// ─── Mock parser ──────────────────────────────────────────────────────────────
function detectSourceType(raw: string): SourceType {
  const lower = raw.toLowerCase();
  if (lower.includes('doi') || lower.includes('journal') || lower.includes('vol.') || lower.includes('pp.')) return 'journal';
  if (lower.includes('http') || lower.includes('www.') || lower.includes('retrieved')) return 'website';
  if (lower.includes('conference') || lower.includes('proceedings') || lower.includes('symposium')) return 'conference';
  if (lower.includes('thesis') || lower.includes('dissertation') || lower.includes('university')) return 'thesis';
  if (lower.includes('newspaper') || lower.includes('times') || lower.includes('post') || lower.includes('guardian')) return 'newspaper';
  return 'book';
}

function parseAuthors(raw: string): string[] {
  // Try to extract author-like patterns: "Last, F." or "First Last"
  const patterns = [
    /([A-Z][a-z]+,\s*[A-Z]\.(?:\s*[A-Z]\.)?)/g,
    /([A-Z][a-z]+\s+[A-Z][a-z]+)/g,
  ];
  for (const pattern of patterns) {
    const matches = raw.match(pattern);
    if (matches && matches.length > 0) return matches.slice(0, 4);
  }
  return ['Unknown Author'];
}

function parseYear(raw: string): string {
  const match = raw.match(/\b(19|20)\d{2}\b/);
  return match ? match[0] : 'n.d.';
}

function parseTitle(raw: string): string {
  // Look for quoted title or title-case phrase
  const quoted = raw.match(/[""]([^"""]+)["""]/);
  if (quoted) return quoted[1];
  // Fallback: extract a plausible title segment
  const cleaned = raw.replace(/\b(19|20)\d{2}\b/, '').replace(/[A-Z][a-z]+,\s*[A-Z]\./g, '').trim();
  const words = cleaned.split(/\s+/).slice(0, 10).join(' ');
  return words || 'Untitled';
}

function parseJournal(raw: string): string | undefined {
  const match = raw.match(/(?:in|journal of|review of|proceedings of)\s+([A-Z][^,.]+)/i);
  return match ? match[1].trim() : undefined;
}

function parseVolume(raw: string): string | undefined {
  const match = raw.match(/[Vv]ol(?:ume)?\.?\s*(\d+)/);
  return match ? match[1] : undefined;
}

function parseIssue(raw: string): string | undefined {
  const match = raw.match(/[Nn]o\.?\s*(\d+)|[Ii]ssue\s*(\d+)/);
  return match ? (match[1] || match[2]) : undefined;
}

function parsePages(raw: string): string | undefined {
  const match = raw.match(/pp?\.?\s*(\d+[-–]\d+)/);
  return match ? match[1] : undefined;
}

function parseDOI(raw: string): string | undefined {
  const match = raw.match(/doi:\s*(10\.\S+)/i) || raw.match(/(10\.\d{4,}\/\S+)/);
  return match ? match[1] : undefined;
}

function parseURL(raw: string): string | undefined {
  const match = raw.match(/https?:\/\/[^\s]+/);
  return match ? match[0] : undefined;
}

function parsePublisher(raw: string): string | undefined {
  const match = raw.match(/(?:Press|Publishers?|Publishing|Inc\.|Ltd\.|LLC)[^,.]*/i);
  return match ? match[0].trim() : undefined;
}

function parseCity(raw: string): string | undefined {
  const cities = ['New York', 'London', 'Chicago', 'Boston', 'Oxford', 'Cambridge', 'San Francisco', 'Los Angeles', 'Toronto', 'Sydney'];
  for (const city of cities) {
    if (raw.includes(city)) return city;
  }
  return undefined;
}

function parseReference(raw: string, id: string): ParsedReference {
  return {
    id,
    raw,
    sourceType: detectSourceType(raw),
    authors: parseAuthors(raw),
    year: parseYear(raw),
    title: parseTitle(raw),
    journal: parseJournal(raw),
    volume: parseVolume(raw),
    issue: parseIssue(raw),
    pages: parsePages(raw),
    doi: parseDOI(raw),
    url: parseURL(raw),
    publisher: parsePublisher(raw),
    city: parseCity(raw),
  };
}

// ─── Citation formatters ──────────────────────────────────────────────────────
function formatAPA(ref: ParsedReference): FormattedCitation {
  const authorStr = ref.authors.length === 1
    ? ref.authors[0]
    : ref.authors.length === 2
    ? `${ref.authors[0]} & ${ref.authors[1]}`
    : ref.authors.length > 2
    ? `${ref.authors[0]}, ${ref.authors[1]}, & ${ref.authors[ref.authors.length - 1]}`
    : 'Unknown Author';

  let full = '';
  if (ref.sourceType === 'journal') {
    full = `${authorStr} (${ref.year}). ${ref.title}. ${ref.journal ?? 'Journal'}${ref.volume ? `, ${ref.volume}` : ''}${ref.issue ? `(${ref.issue})` : ''}${ref.pages ? `, ${ref.pages}` : ''}.${ref.doi ? ` https://doi.org/${ref.doi}` : ''}`;
  } else if (ref.sourceType === 'website') {
    full = `${authorStr} (${ref.year}). ${ref.title}. Retrieved from ${ref.url ?? 'URL'}`;
  } else if (ref.sourceType === 'conference') {
    full = `${authorStr} (${ref.year}). ${ref.title}. In *Proceedings of the Conference*${ref.pages ? ` (pp. ${ref.pages})` : ''}.`;
  } else if (ref.sourceType === 'thesis') {
    full = `${authorStr} (${ref.year}). *${ref.title}* [Doctoral dissertation]. ${ref.institution ?? 'University'}.`;
  } else {
    full = `${authorStr} (${ref.year}). *${ref.title}*. ${ref.city ? `${ref.city}: ` : ''}${ref.publisher ?? 'Publisher'}.`;
  }

  const lastName = ref.authors[0]?.split(',')[0] ?? 'Author';
  const inText = ref.authors.length > 2
    ? `(${lastName} et al., ${ref.year})`
    : `(${ref.authors.map(a => a.split(',')[0]).join(' & ')}, ${ref.year})`;

  return { style: 'APA', fullCitation: full, inTextCitation: inText };
}

function formatMLA(ref: ParsedReference): FormattedCitation {
  const authorStr = ref.authors.length === 1
    ? ref.authors[0]
    : `${ref.authors[0]}, et al.`;

  let full = '';
  if (ref.sourceType === 'journal') {
    full = `${authorStr}. "${ref.title}." *${ref.journal ?? 'Journal'}*${ref.volume ? `, vol. ${ref.volume}` : ''}${ref.issue ? `, no. ${ref.issue}` : ''}, ${ref.year}${ref.pages ? `, pp. ${ref.pages}` : ''}.${ref.doi ? ` DOI: ${ref.doi}.` : ''}`;
  } else if (ref.sourceType === 'website') {
    full = `${authorStr}. "${ref.title}." *Website*, ${ref.year}, ${ref.url ?? 'URL'}.`;
  } else if (ref.sourceType === 'conference') {
    full = `${authorStr}. "${ref.title}." *Conference Proceedings*, ${ref.year}${ref.pages ? `, pp. ${ref.pages}` : ''}.`;
  } else if (ref.sourceType === 'thesis') {
    full = `${authorStr}. *${ref.title}*. ${ref.year}. ${ref.institution ?? 'University'}, PhD dissertation.`;
  } else {
    full = `${authorStr}. *${ref.title}*. ${ref.city ? `${ref.city}: ` : ''}${ref.publisher ?? 'Publisher'}, ${ref.year}.`;
  }

  const lastName = ref.authors[0]?.split(',')[0] ?? 'Author';
  const inText = ref.authors.length > 1
    ? `(${lastName} et al. ${ref.pages ?? ref.year})`
    : `(${lastName} ${ref.pages ?? ref.year})`;

  return { style: 'MLA', fullCitation: full, inTextCitation: inText };
}

function formatChicago(ref: ParsedReference): FormattedCitation {
  const authorStr = ref.authors.length === 1
    ? ref.authors[0]
    : ref.authors.length === 2
    ? `${ref.authors[0]} and ${ref.authors[1]}`
    : `${ref.authors[0]} et al.`;

  let full = '';
  if (ref.sourceType === 'journal') {
    full = `${authorStr}. "${ref.title}." *${ref.journal ?? 'Journal'}* ${ref.volume ?? ''}${ref.issue ? `, no. ${ref.issue}` : ''} (${ref.year})${ref.pages ? `: ${ref.pages}` : ''}.${ref.doi ? ` https://doi.org/${ref.doi}.` : ''}`;
  } else if (ref.sourceType === 'website') {
    full = `${authorStr}. "${ref.title}." Accessed ${ref.year}. ${ref.url ?? 'URL'}.`;
  } else if (ref.sourceType === 'conference') {
    full = `${authorStr}. "${ref.title}." Paper presented at the Conference, ${ref.year}${ref.pages ? `, ${ref.pages}` : ''}.`;
  } else if (ref.sourceType === 'thesis') {
    full = `${authorStr}. "${ref.title}." PhD diss., ${ref.institution ?? 'University'}, ${ref.year}.`;
  } else {
    full = `${authorStr}. *${ref.title}*. ${ref.city ? `${ref.city}: ` : ''}${ref.publisher ?? 'Publisher'}, ${ref.year}.`;
  }

  const lastName = ref.authors[0]?.split(',')[0] ?? 'Author';
  const inText = `(${lastName} ${ref.year}${ref.pages ? `, ${ref.pages}` : ''})`;

  return { style: 'Chicago', fullCitation: full, inTextCitation: inText };
}

function formatIEEE(ref: ParsedReference, index: number): FormattedCitation {
  const authorStr = ref.authors.map(a => {
    const parts = a.split(',');
    const last = parts[0]?.trim() ?? '';
    const initials = parts[1]?.trim() ?? '';
    return `${initials} ${last}`.trim();
  }).join(', ');

  let full = '';
  if (ref.sourceType === 'journal') {
    full = `${authorStr}, "${ref.title}," *${ref.journal ?? 'Journal'}*${ref.volume ? `, vol. ${ref.volume}` : ''}${ref.issue ? `, no. ${ref.issue}` : ''}${ref.pages ? `, pp. ${ref.pages}` : ''}, ${ref.year}.${ref.doi ? ` doi: ${ref.doi}.` : ''}`;
  } else if (ref.sourceType === 'website') {
    full = `${authorStr}. "${ref.title}." [Online]. Available: ${ref.url ?? 'URL'}. [Accessed: ${ref.year}].`;
  } else if (ref.sourceType === 'conference') {
    full = `${authorStr}, "${ref.title}," in *Proc. Conference*${ref.pages ? `, pp. ${ref.pages}` : ''}, ${ref.year}.`;
  } else if (ref.sourceType === 'thesis') {
    full = `${authorStr}, "${ref.title}," Ph.D. dissertation, ${ref.institution ?? 'Univ.'}, ${ref.year}.`;
  } else {
    full = `${authorStr}, *${ref.title}*. ${ref.city ? `${ref.city}: ` : ''}${ref.publisher ?? 'Publisher'}, ${ref.year}.`;
  }

  return { style: 'IEEE', fullCitation: full, inTextCitation: `[${index + 1}]` };
}

function formatCitation(ref: ParsedReference, style: CitationStyle, index: number): FormattedCitation {
  switch (style) {
    case 'APA': return formatAPA(ref);
    case 'MLA': return formatMLA(ref);
    case 'Chicago': return formatChicago(ref);
    case 'IEEE': return formatIEEE(ref, index);
  }
}

// ─── Sample references ────────────────────────────────────────────────────────
const SAMPLE_REFS = `Johnson, M. A., & Smith, B. C. (2022). The impact of AI on educational outcomes. Journal of Educational Technology, 15(3), pp. 45-67. doi:10.1234/jet.2022.15.3.45

Brown, T. (2021). Deep learning in practice. New York: Oxford University Press.

Williams, R., Davis, K., & Lee, J. (2023). "Machine learning applications in healthcare." Retrieved from https://www.healthtech.org/ml-healthcare

Chen, X., & Park, S. (2020). Neural networks for natural language processing. In Proceedings of the International Conference on AI, pp. 112-125.

Thompson, A. (2019). Adaptive learning systems in higher education. PhD dissertation, Stanford University.`;

// ─── Source type config ───────────────────────────────────────────────────────
const SOURCE_CONFIG: Record<SourceType, { icon: string; label: string; color: string; bg: string }> = {
  journal:    { icon: 'ri-article-line',      label: 'Journal Article', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  book:       { icon: 'ri-book-2-line',        label: 'Book',            color: 'text-amber-600',  bg: 'bg-amber-50' },
  website:    { icon: 'ri-global-line',         label: 'Website',         color: 'text-cyan-600',   bg: 'bg-cyan-50' },
  conference: { icon: 'ri-presentation-line',  label: 'Conference',      color: 'text-emerald-600',bg: 'bg-emerald-50' },
  thesis:     { icon: 'ri-graduation-cap-line', label: 'Thesis',          color: 'text-purple-600', bg: 'bg-purple-50' },
  newspaper:  { icon: 'ri-newspaper-line',      label: 'Newspaper',       color: 'text-orange-600', bg: 'bg-orange-50' },
};

const STYLE_CONFIG: Record<CitationStyle, { color: string; bg: string; border: string; desc: string }> = {
  APA:     { color: 'text-indigo-700',  bg: 'bg-indigo-50',  border: 'border-indigo-300',  desc: 'Social sciences, psychology, education' },
  MLA:     { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300', desc: 'Humanities, literature, arts' },
  Chicago: { color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-300',   desc: 'History, arts, social sciences' },
  IEEE:    { color: 'text-cyan-700',    bg: 'bg-cyan-50',    border: 'border-cyan-300',    desc: 'Engineering, computer science, tech' },
};

// ─── Citation Card ────────────────────────────────────────────────────────────
function CitationCard({ ref: parsedRef, citations, index, isExpanded, onToggle }: {
  ref: ParsedReference;
  citations: FormattedCitation[];
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const [copiedStyle, setCopiedStyle] = useState<CitationStyle | null>(null);
  const srcCfg = SOURCE_CONFIG[parsedRef.sourceType];

  const handleCopy = (text: string, style: CitationStyle) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedStyle(style);
      setTimeout(() => setCopiedStyle(null), 2000);
    });
  };

  return (
    <div className={`rounded-2xl border-2 overflow-hidden transition-all ${isExpanded ? 'border-indigo-200 shadow-md' : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'}`}>
      {/* Header */}
      <div
        className={`flex items-center gap-3 p-4 cursor-pointer ${isExpanded ? 'bg-gradient-to-r from-indigo-50 to-purple-50' : 'bg-white'}`}
        onClick={onToggle}
      >
        <div className={`w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 ${srcCfg.bg}`}>
          <i className={`${srcCfg.icon} ${srcCfg.color} text-sm`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${srcCfg.bg} ${srcCfg.color}`}>{srcCfg.label}</span>
            <span className="text-xs text-gray-400">#{index + 1}</span>
          </div>
          <p className="text-sm font-semibold text-gray-800 truncate">{parsedRef.title}</p>
          <p className="text-xs text-gray-400 truncate">{parsedRef.authors.join(', ')} · {parsedRef.year}</p>
        </div>
        <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}></i>
      </div>

      {/* Expanded: formatted citations */}
      {isExpanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {citations.map((cit) => {
            const styleCfg = STYLE_CONFIG[cit.style];
            return (
              <div key={cit.style} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-extrabold px-2.5 py-1 rounded-full border ${styleCfg.bg} ${styleCfg.color} ${styleCfg.border}`}>
                      {cit.style}
                    </span>
                    <span className="text-xs text-gray-400">{styleCfg.desc}</span>
                  </div>
                  <button
                    onClick={() => handleCopy(cit.fullCitation, cit.style)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${copiedStyle === cit.style ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    <i className={`${copiedStyle === cit.style ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`}></i>
                    {copiedStyle === cit.style ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                {/* Full citation */}
                <div className="bg-gray-50 rounded-xl px-4 py-3 mb-2 border border-gray-200">
                  <p className="text-xs text-gray-700 leading-relaxed font-mono"
                    dangerouslySetInnerHTML={{ __html: cit.fullCitation.replace(/\*(.*?)\*/g, '<em>$1</em>') }}
                  />
                </div>

                {/* In-text */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 font-medium flex-shrink-0">In-text:</span>
                  <code className={`text-xs font-mono px-2 py-0.5 rounded-lg ${styleCfg.bg} ${styleCfg.color} border ${styleCfg.border}`}>
                    {cit.inTextCitation}
                  </code>
                  <button
                    onClick={() => handleCopy(cit.inTextCitation, cit.style)}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    copy
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CitationPanel() {
  const [rawInput, setRawInput] = useState(SAMPLE_REFS);
  const [parsedRefs, setParsedRefs] = useState<ParsedReference[]>([]);
  const [isFormatting, setIsFormatting] = useState(false);
  const [hasFormatted, setHasFormatted] = useState(false);
  const [selectedStyles, setSelectedStyles] = useState<CitationStyle[]>(['APA', 'MLA', 'Chicago', 'IEEE']);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState<CitationStyle | null>(null);

  const toggleStyle = (style: CitationStyle) => {
    setSelectedStyles(prev =>
      prev.includes(style)
        ? prev.length > 1 ? prev.filter(s => s !== style) : prev
        : [...prev, style]
    );
  };

  const handleFormat = useCallback(() => {
    if (!rawInput.trim()) return;
    setIsFormatting(true);
    setHasFormatted(false);
    setParsedRefs([]);
    setExpandedId(null);

    setTimeout(() => {
      const lines = rawInput.split('\n').filter(l => l.trim().length > 10);
      const refs = lines.map((line, i) => parseReference(line.trim(), `ref-${i}`));
      setParsedRefs(refs);
      setIsFormatting(false);
      setHasFormatted(true);
      if (refs.length > 0) setExpandedId(refs[0].id);
    }, 1200);
  }, [rawInput]);

  const handleCopyAll = (style: CitationStyle) => {
    const allCitations = parsedRefs
      .map((ref, i) => formatCitation(ref, style, i).fullCitation)
      .join('\n\n');
    navigator.clipboard.writeText(allCitations).then(() => {
      setCopiedAll(style);
      setTimeout(() => setCopiedAll(null), 2000);
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex-shrink-0">
          <i className="ri-double-quotes-l text-white text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-gray-900">Citation Formatter</p>
          <p className="text-xs text-gray-500">Auto-converts raw references → APA · MLA · Chicago · IEEE</p>
        </div>
        {hasFormatted && (
          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 flex-shrink-0">
            {parsedRefs.length} formatted
          </span>
        )}
      </div>

      {/* Style Selector */}
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Output Styles</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(['APA', 'MLA', 'Chicago', 'IEEE'] as CitationStyle[]).map(style => {
            const cfg = STYLE_CONFIG[style];
            const isSelected = selectedStyles.includes(style);
            return (
              <button
                key={style}
                type="button"
                onClick={() => toggleStyle(style)}
                className={`flex flex-col items-start gap-1 p-3 rounded-2xl border-2 text-left transition-all cursor-pointer ${isSelected ? `${cfg.border} ${cfg.bg} shadow-sm` : 'border-gray-200 bg-white hover:border-gray-300'}`}
              >
                <div className="flex items-center justify-between w-full">
                  <span className={`text-sm font-extrabold ${isSelected ? cfg.color : 'text-gray-600'}`}>{style}</span>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? `${cfg.border} bg-white` : 'border-gray-300'}`}>
                    {isSelected && <div className={`w-2 h-2 rounded-full ${cfg.bg.replace('bg-', 'bg-').replace('-50', '-500')}`}></div>}
                  </div>
                </div>
                <span className="text-xs text-gray-400 leading-tight">{cfg.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Input */}
      <div className="rounded-2xl border-2 border-gray-200 overflow-hidden bg-white">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-bold text-gray-600 flex items-center gap-2">
            <i className="ri-list-unordered text-indigo-500"></i>
            Raw References
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{rawInput.split('\n').filter(l => l.trim().length > 10).length} references detected</span>
            {hasFormatted && (
              <button
                onClick={() => { setHasFormatted(false); setParsedRefs([]); }}
                className="text-xs text-indigo-600 font-semibold hover:text-indigo-800 cursor-pointer whitespace-nowrap"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {!hasFormatted ? (
          <textarea
            value={rawInput}
            onChange={(e) => { if (e.target.value.length <= 2500) setRawInput(e.target.value); }}
            placeholder={`Paste your raw references here, one per line. Example:\n\nJohnson, M. A. (2022). The impact of AI. Journal of Tech, 15(3), 45-67.\n\nBrown, T. (2021). Deep learning. New York: Oxford University Press.`}
            rows={10}
            className="w-full px-5 py-4 text-sm text-gray-800 placeholder-gray-400 focus:outline-none resize-none leading-relaxed font-mono"
          />
        ) : (
          <div className="px-5 py-4 min-h-[120px]">
            <div className="space-y-1">
              {rawInput.split('\n').filter(l => l.trim().length > 10).map((line, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-xs font-bold text-indigo-400 mt-0.5 flex-shrink-0 w-5 text-right">{i + 1}.</span>
                  <p className="text-xs text-gray-600 leading-relaxed font-mono">{line.trim()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end px-4 py-3 bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleFormat}
            disabled={isFormatting || !rawInput.trim()}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-bold rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFormatting ? (
              <><i className="ri-loader-4-line animate-spin text-base"></i> Formatting…</>
            ) : (
              <><i className="ri-magic-line text-base"></i> {hasFormatted ? 'Re-Format' : 'Format Citations'}</>
            )}
          </button>
        </div>
      </div>

      {/* Formatting progress */}
      {isFormatting && (
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-purple-50 p-5">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-loader-4-line animate-spin text-indigo-500 text-lg"></i>
            <p className="text-sm font-bold text-indigo-800">Parsing &amp; Formatting References…</p>
          </div>
          <div className="space-y-2">
            {['Detecting source types…', 'Extracting authors, titles, years…', 'Applying APA, MLA, Chicago, IEEE rules…', 'Generating in-text citations…'].map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center rounded-full bg-indigo-100 flex-shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></div>
                </div>
                <span className="text-xs text-indigo-700 font-medium">{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {hasFormatted && parsedRefs.length > 0 && (
        <div className="space-y-4">
          {/* Copy All row */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h4 className="text-sm font-extrabold text-gray-800 flex items-center gap-2">
              <i className="ri-double-quotes-l text-indigo-500"></i>
              Formatted Citations
              <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">{parsedRefs.length}</span>
            </h4>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400 font-medium">Copy all as:</span>
              {selectedStyles.map(style => {
                const cfg = STYLE_CONFIG[style];
                return (
                  <button
                    key={style}
                    onClick={() => handleCopyAll(style)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all whitespace-nowrap cursor-pointer ${copiedAll === style ? 'bg-emerald-500 text-white border-emerald-500' : `${cfg.bg} ${cfg.color} ${cfg.border} hover:shadow-sm`}`}
                  >
                    <i className={`${copiedAll === style ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`}></i>
                    {copiedAll === style ? 'Copied!' : style}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Citation cards */}
          <div className="space-y-3">
            {parsedRefs.map((ref, i) => (
              <CitationCard
                key={ref.id}
                ref={ref}
                citations={selectedStyles.map(style => formatCitation(ref, style, i))}
                index={i}
                isExpanded={expandedId === ref.id}
                onToggle={() => setExpandedId(expandedId === ref.id ? null : ref.id)}
              />
            ))}
          </div>

          {/* Info */}
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
              <i className="ri-information-line text-amber-600 text-base"></i>
            </div>
            <p className="text-xs text-amber-700 leading-relaxed">
              <strong>Always verify formatted citations</strong> against the official style guide. The formatter uses pattern recognition and may not capture all edge cases — especially for unusual source types or incomplete references.
            </p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!hasFormatted && !isFormatting && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-100 flex-shrink-0">
            <i className="ri-lightbulb-line text-indigo-600 text-base"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-indigo-800 mb-1">How Citation Formatter Works</p>
            <p className="text-xs text-indigo-700 leading-relaxed">
              Paste any raw references — one per line — and click <strong>Format Citations</strong>. The engine auto-detects source types (journal, book, website, conference, thesis) and converts each reference into all selected styles simultaneously. Click any result to expand and copy individual citations or in-text references.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
